import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ButtonWrapper, DropdownIcon, HiddenSelectElement, Placeholder, BKJSelectIcon, BKJSelectErrorMessage, } from './BKJSelect.styled.js';
import { v4 as uuidv4 } from 'uuid';
import { usePopper } from 'react-popper';
import { getDesignLibraryElementNode } from './utils/getDesignLibraryElementNode.js';
import { useClickOutside } from '../../hooks/useClickOutside.js';
import { mergeRefs } from './utils/mergeRefs.js';
import { BKJDropdown } from '../BKJDropdown/index.js';
import { isArray } from '../../common.types.js';
import { ErrorBoundary } from '../ErrorBoundary/index.js';
import { BKJTheme } from '../../providers/index.js';
import { Portal } from '../Portal/Portal.js';
import { BKJIcon } from '../BKJIcon/index.js';
import { VariantsWithClear } from './BKJSelect.constants.js';
function BKJSelectEndIcon({ iconProps, isOpen, value, name, variant, onChange, }) {
    if (variant === 'PurpleTextNoArrow')
        return null;
    const handleClear = (e) => {
        e.stopPropagation();
        onChange({ target: { value: [], name } });
    };
    if (value.length >= 1 && VariantsWithClear.includes(variant)) {
        return _jsx(BKJIcon, { iconName: "CloseBold", color: "White", onClick: handleClear });
    }
    return (_jsx(DropdownIcon, { ...iconProps, iconName: "ArrowDown", color: BKJTheme.select[variant].colorScheme.fontColor(isOpen), isOpen: isOpen }));
}
function BKJSelectComponent(props, ref) {
    const { type = 'Single', label, name, onChange, onClose, value: _value = [], placeholder, required = false, variant = 'GreyOutlined', width = 'auto', disabled, dropdownVariant = 'Square', dropdownPlacement = 'bottom', moreValuesLabel = 'More', dropdownWidth, dropdownHeight, dropdownMinHeight, options, renderOptions: RenderOptions, iconComponent: IconComponent, iconProps = {}, modifiers: _modifiers, error, dropdownProps, placeholderColor, ...rest } = props;
    const isError = !!error || (Array.isArray(error) && !!error[0]);
    let errorMessages = [];
    if (error && typeof error !== 'boolean') {
        errorMessages = Array.isArray(error) ? [...error] : [error];
    }
    if (!Array.isArray(_value))
        throw new Error("BKJSelect must receive an array for it's value. Even if type is set to Single.");
    const value = useMemo(() => (Boolean(_value.length) ? _value : []), [_value]);
    const [displayValues, setDisplayValues] = useState(options.filter((opt) => value.includes(opt.value)).map((opt) => opt.label));
    const modifiers = useMemo(() => _modifiers || [
        {
            name: 'offset',
            options: {
                offset: () => {
                    switch (dropdownVariant) {
                        case 'SeamlessRounded':
                            return [0, -2];
                        case 'Square':
                            return [0, 4];
                        default:
                            return [0, 0];
                    }
                },
            },
        },
        {
            name: 'sameWidth',
            enabled: !dropdownWidth,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn({ state }) {
                if (dropdownWidth)
                    return void 0;
                state.styles.popper.minWidth = `${state.rects.reference.width}px`;
                state.styles.popper.maxWidth = `${state.rects.reference.width}px`;
            },
            effect({ state }) {
                if (dropdownWidth)
                    return void 0;
                state.elements.popper.style.minWidth = `${state.elements.reference.offsetWidth}px`;
                state.elements.popper.style.maxWidth = `${state.elements.reference.offsetWidth}px`;
            },
        },
    ], [dropdownVariant, _modifiers]);
    const uuid = useMemo(() => `${name}_${uuidv4()}`, [name]);
    const buttonRef = useRef();
    const dropdownRef = useRef();
    const [inputElement, setInputElement] = useState();
    const [dropdownElement, setDropdownElement] = useState();
    const [targetElement, setTargetElement] = useState(getDesignLibraryElementNode('dropdown-root'));
    const [isOpen, setOpen] = useState(false);
    useEffect(() => {
        if (!targetElement) {
            const element = document.createElement('div');
            element.setAttribute('id', 'dropdown-root');
            document.body.prepend(element);
            setTargetElement(element);
        }
    }, []); // eslint-disable-line
    const handleChange = useCallback((event) => {
        switch (type) {
            case 'Multiple': {
                let nextValue = [...value];
                if (Array.isArray(event.target.value)) {
                    setDisplayValues(event.target.label);
                    onChange({
                        ...event,
                        target: { ...event.target, value: [...new Set(event.target.value)] },
                    });
                    return;
                }
                if (nextValue.includes(event.target.value)) {
                    nextValue = nextValue.filter((str) => str !== event.target.value);
                    setDisplayValues(displayValues.filter((str) => str !== event.target.label));
                }
                else {
                    nextValue = nextValue.concat(event.target.value);
                    const nextDisplayValues = nextValue.length === 1
                        ? [event.target.label]
                        : displayValues.concat(event.target.label);
                    setDisplayValues([...new Set(nextDisplayValues)]);
                }
                onChange({ ...event, target: { ...event.target, value: [...new Set(nextValue)] } });
                break;
            }
            case 'Single': {
                onChange({ ...event, target: { ...event.target, value: [event.target.value] } });
                setDisplayValues([event.target.label]);
                setOpen(false);
                break;
            }
        }
    }, [displayValues, onChange, type, value]);
    const handleToggle = useCallback(() => {
        setOpen(!isOpen);
    }, [isOpen]);
    const handleClose = useCallback((event) => {
        if (buttonRef?.current && !buttonRef?.current.contains(event.target)) {
            if (dropdownRef?.current && !dropdownRef?.current.contains(event.target)) {
                setOpen(false);
                onClose && onClose(event);
            }
        }
    }, [onClose]);
    const { styles, attributes } = usePopper(inputElement, dropdownElement, {
        modifiers,
        placement: dropdownPlacement,
    });
    const popperStyles = useMemo(() => ({
        ...styles['popper'],
        maxHeight: 'auto',
        height: 'auto',
        minHeight: 'auto',
    }), [styles]);
    useClickOutside([buttonRef && buttonRef.current, dropdownRef && dropdownRef.current, dropdownElement], handleClose);
    const popperProps = useMemo(() => ({
        style: popperStyles,
        ...attributes['popper'],
    }), [attributes, popperStyles]);
    let isPlaceholderShown = Boolean(value.length === 0) && !!placeholder;
    if (isArray(value)) {
        isPlaceholderShown = Boolean(value.length === 0) && !!placeholder;
    }
    if (isArray(value) && value.length === 1 && value[0] === '') {
        isPlaceholderShown = !!placeholder;
    }
    if (typeof value === 'string') {
        isPlaceholderShown = value === '' && !!placeholder;
    }
    const scrollRef = useRef();
    const RenderOptionsComponent = RenderOptions;
    return (_jsxs(ErrorBoundary, { componentName: "BKJSelect", height: "38px", direction: "row", size: "Small", width: width, isStyled: true, children: [_jsxs(ButtonWrapper, { ...rest, disabled: disabled, "$isPlaceholderShown": isPlaceholderShown, ref: mergeRefs([ref, buttonRef, setInputElement]), name: name, onClick: handleToggle, value: value, htmlFor: uuid, variant: variant, dropdownVariant: dropdownVariant, width: width, isOpen: isOpen, isError: isError, type: "button", children: [IconComponent && iconProps.iconName && _jsx(BKJSelectIcon, { ...iconProps }), _jsx(HiddenSelectElement, { readOnly: true, multiple: true, id: uuid, name: name, value: value, role: "listbox", required: required, "aria-labelledby": uuid }), isPlaceholderShown ? (_jsx(Placeholder, { placeholderColor: placeholderColor, children: placeholder })) : typeof label === 'undefined' ? (_jsx("span", { children: displayValues.length > 0
                            ? `${displayValues[0]}${displayValues.length > 1
                                ? `... +${displayValues.length - 1} ${moreValuesLabel}`
                                : ''}`
                            : placeholder })) : (_jsx("span", { children: label })), _jsx(BKJSelectEndIcon, { iconProps: iconProps, isOpen: isOpen, value: value, name: name, variant: variant, onChange: (e) => {
                            setDisplayValues([]);
                            return onChange(e);
                        } })] }), errorMessages[0] &&
                errorMessages.map((err) => _jsx(BKJSelectErrorMessage, { children: err }, err)), targetElement && isOpen && (_jsx(Portal, { targetElement: targetElement, children: _jsx(BKJDropdown, { "$dropdownVariant": dropdownVariant, "$dropdownWidth": dropdownWidth, "$dropdownHeight": dropdownHeight, "$dropdownMinHeight": dropdownMinHeight, ...popperProps, ref: mergeRefs([dropdownRef, setDropdownElement]), children: _jsx(RenderOptionsComponent, { scrollRef: scrollRef, onChange: handleChange, selected: value, options: options, onClose: handleClose, name: name, type: type, dropdownProps: dropdownProps }) }) }))] }));
}
export const BKJSelect = forwardRef(BKJSelectComponent);
//# sourceMappingURL=BKJSelect.js.map